// Zählquellen der drei Limits als Query-Optionen für react-query. Die Hooks in
// hooks.js hängen enabled und staleTime je Modus an, die Guards zählen damit
// per fetchQuery frisch. Gezählt wird nur über die reinen Module in usage/,
// hier steht keine eigene Zählregel.
//
// Stellen laufen über eine eigene leichte Query und nie über useCareersQuery
// oder QUERY_KEYS.careersByPartner, denn dessen queryFn löscht die
// Produkt-Tour-Stelle als Seiteneffekt. Zugänge und Talentspeicher teilen sich
// Key und queryFn mit der Mitgliederliste bzw. der Talentliste und damit deren
// Cache.

import { QUERY_KEYS } from 'constants/constants'
import { query } from '../../graphql'
import { careerActiveFilterInput } from '../../graphql/filter-inputs'
import { talentsQueryOptions } from '../../hooks/use-talents-query'
import { teamMembersQueryOptions } from '../../hooks/use-team-members-query'
import { countActiveCareers } from './usage/careers'
import { countSeats } from './usage/seats'
import { rankTalentStorageCached } from './usage/talent-storage'

export { talentsQueryOptions }

// Meter und Banner dürfen so alt sein. Guards übergeben staleTime 0.
export const USAGE_STALE_TIME = 30 * 1000

// Alle Seiten werden geladen, query() folgt dem nextToken. Der Filter auf
// ACTIVE läuft in DynamoDB nach dem limit, Seiten können also leer sein.
// Archivierte, Tour-, Beispiel- und versionierte Stellen filtert erst
// countsAsActiveCareer. Ohne Daten (null) gilt die Zählung als fehlgeschlagen
// und nicht als 0, die Guards sind dann fail-open mit COUNT_FAILED.
export const fetchCareerUsage = async (partnerId) => {
  if (!partnerId) throw new Error('pricing usage careers: partnerId fehlt')

  const careers = await query({
    query: 'careerUsageByPartnerByDate',
    variables: { partner_id: partnerId, filter: careerActiveFilterInput },
    authMode: 'AMAZON_COGNITO_USER_POOLS',
    options: { origin: 'careerByPartnerByDate' }
  })

  if (!Array.isArray(careers)) {
    throw new Error('pricing usage careers: keine Daten')
  }

  return careers
}

// Daten sind die rohen Stellen, damit careerSlotDelta sie als activeCareers
// bekommt. Die Zahl liefert countUsage.
export const careerUsageQueryOptions = ({ partnerId }) => ({
  queryKey: QUERY_KEYS.careerUsageByPartner(partnerId),
  queryFn: () => fetchCareerUsage(partnerId),
  staleTime: USAGE_STALE_TIME
})

// Gleicher Key und gleiche queryFn wie die Mitgliederliste unter
// /settings/user. Daten sind die rohen EMPLOYEE-Spaces.
export const seatUsageQueryOptions = ({ partnerId }) => ({
  ...teamMembersQueryOptions({ partnerId }),
  staleTime: USAGE_STALE_TIME
})

// Gleicher Key und gleiche queryFn wie useTalentsQuery, also auch dieselbe
// Delta-Logik. Daten sind die rohen [spaces, careers] ohne select, gerankt
// wird mit rankTalentStorageCached auf spaces.
export const talentStorageUsageQueryOptions = ({
  partnerId,
  username,
  queryClient
}) => ({
  ...talentsQueryOptions({ partnerId, username, queryClient }),
  staleTime: USAGE_STALE_TIME
})

// Je Ressource (Keys wie RESOURCES in plans.js) Optionen, Zählung und das
// Key-Präfix für invalidateQueries. Der Talents-Key enthält zusätzlich den
// username, das Präfix trifft alle Nutzer:innen des Partners.
const USAGE_SOURCES = {
  careers: {
    options: careerUsageQueryOptions,
    count: (data) => countActiveCareers(data),
    keyPrefix: (partnerId) => QUERY_KEYS.careerUsageByPartner(partnerId)
  },
  seats: {
    options: seatUsageQueryOptions,
    count: (data) => countSeats(data),
    keyPrefix: (partnerId) => QUERY_KEYS.userManagementByPartner(partnerId)
  },
  talentStorage: {
    options: talentStorageUsageQueryOptions,
    count: (data) => rankTalentStorageCached(data?.[0]).used,
    keyPrefix: (partnerId) =>
      QUERY_KEYS.talentsByPartner(partnerId, '').slice(0, 2)
  }
}

const sourceOf = (resource) =>
  Object.prototype.hasOwnProperty.call(USAGE_SOURCES, resource)
    ? USAGE_SOURCES[resource]
    : null

// Eine unbekannte Ressource ergibt null, der Aufrufer zählt dann nicht.
export const usageQueryOptions = (resource, context = {}) =>
  sourceOf(resource)?.options(context) ?? null

// Belegte Menge aus den Query-Daten. Ohne Daten null (noch nicht gezählt), so
// entsteht über resolveLimitState der Zustand pending statt 0.
export const countUsage = (resource, data) => {
  const source = sourceOf(resource)
  if (!source || data == null) return null

  return source.count(data)
}

export const usageQueryKeyPrefix = (resource, partnerId) =>
  sourceOf(resource)?.keyPrefix(partnerId) ?? null
